// Which of one day's stage zones can be copied onto another day, and
// which cannot.
//
// "Copy from Day N" used to copy every polygon unconditionally, rewrite
// its stage slug to the target day and insert it straight into
// festival_areas. Two ways that went wrong, both seen in real data:
//
//   • The target day may have FEWER stages. HOH / 7TH 26 had 14 stages
//     on day 1 and 13 on day 2; the fourteenth zone landed on day 2
//     belonging to no stage, so it had no label to borrow and showed up
//     on the crew map as "day1_stage14".
//   • The target day may have NO stages at all (the producer hasn't
//     filled in step 2 yet). Every zone then landed orphaned, while the
//     panel — which lists stages, not zones — showed nothing at all. The
//     button looked broken and silently wrote rows.
//
// So the decision is: a zone can only be copied onto a day that has the
// matching stage. This module makes that decision and nothing else; the
// caller does the writing and the reporting.

/** A zone as it comes out of `festival_areas`, narrowed to what matters here. */
export type PlannableZone = { stage_id: string | null; name: string | null };

/** A wizard stage: synthetic per-day `name` slug plus the producer's `label`. */
export type PlannableStage = { name: string; label: string };

export type ZoneCopyPlan<Z> = {
  /** Zones that have a matching stage on the target day. */
  copies: Array<{ zone: Z; stageSlug: string; label: string }>;
  /** The names of the zones with no matching stage, for the message. */
  skipped: string[];
};

/**
 * The same stage one day over: `day0_stage1` on day 1 is `day1_stage1`
 * on day 2.
 *
 * Anchored at the start, unlike the original inline version: a slug that
 * doesn't begin with a day prefix isn't ours to rewrite, and guessing
 * would silently point the copy at some other stage. Returns null for
 * anything unrecognisable so the caller can skip it.
 */
export function retargetStageSlug(
  stageId: string | null | undefined,
  targetDayIndex: number,
): string | null {
  if (!stageId || !/^day\d+_/.test(stageId)) return null;
  return stageId.replace(/^day\d+_/, `day${targetDayIndex}_`);
}

/**
 * Split the source day's zones into the ones that can be copied and the
 * ones that cannot.
 *
 * `targetDayStages` is the target day's stage list (already filtered by
 * `wizardStagesForDay`), and the copy takes its label from THAT day's
 * stage — a stage renamed on day 2 keeps day 2's name, not day 1's.
 */
export function planZoneCopy<Z extends PlannableZone>(input: {
  sourceZones: readonly Z[];
  targetDayStages: readonly PlannableStage[];
  targetDayIndex: number;
}): ZoneCopyPlan<Z> {
  const { sourceZones, targetDayStages, targetDayIndex } = input;
  const stageBySlug = new Map(targetDayStages.map((s) => [s.name, s]));

  const plan: ZoneCopyPlan<Z> = { copies: [], skipped: [] };

  for (const zone of sourceZones) {
    const stageSlug = retargetStageSlug(zone.stage_id, targetDayIndex);
    const stage = stageSlug ? stageBySlug.get(stageSlug) : undefined;

    if (!stageSlug || !stage) {
      // Name it the way the producer knows it: the zone's own name is
      // the source day's stage label. Falling back to the slug is a
      // last resort, and better in a message than on the map.
      plan.skipped.push(zone.name || zone.stage_id || "Unnamed zone");
      continue;
    }

    plan.copies.push({ zone, stageSlug, label: stage.label });
  }

  return plan;
}
